import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import { useGetContactInfoQuery, useSubmitPartnershipInquiryMutation } from '../store/siteContentApi';
import heroImg from '../assets/img/v5/partners-hero.jpg';

// The four partner journeys — doctors, labs, phlebotomists, managed labs.
//
// One component, four routes. The reference ships these as four separate
// pages, but they are byte-for-byte the same markup with a different
// segment preselected: its own logic is a single class whose only
// difference between pages is `seg`, `type` and the allowed applicant
// types. So the variants live in SEGMENTS below and the page is built
// once. The reference also reuses one photograph across all four (and the
// hub) — verified identical by checksum — so there is one hero asset.
//
// The sign-up form posts to the existing POST /partnerships endpoint,
// which pre-dates this rebuild and was left orphaned when the old About
// page was replaced. See the note on buildMessage() for the one field
// mismatch.

const TYPES = {
  doctor: 'Doctor',
  clinic: 'Clinic',
  lab: 'Laboratory',
  centre: 'Collection centre',
  phlebo: 'Phlebotomist',
  hlm: 'Hospital (HLM)',
  independent: 'Independent lab',
};

export const SEGMENTS = {
  doctors: {
    badge: 'For doctors & clinics',
    title: 'Order tests.',
    titleAccent: 'We handle the rest.',
    lead: 'Refer patients from the TrustLab Partner app. We collect at home, test in our NABL-accredited lab and send the report to you and your patient.',
    whyHeading: 'Why doctors work with us',
    whyLead: 'Refer patients to TrustLab and we take care of the rest: home collection, testing and a report sent to both you and your patient.',
    cta: 'Sign up as a doctor',
    allow: ['doctor', 'clinic'],
    perks: [
      ['Refer in seconds', 'Send a test request from the TrustLab Partner app. We book the home visit with your patient.'],
      ['Reports to you', 'Every report reaches you and your patient, with critical values phoned to you without delay.'],
      ['Talk to a pathologist', 'Discuss an unusual result or the right test with our pathologists.'],
      ['Specialty on tap', 'Genomics, cytogenetics, flow cytometry and histopathology without sending patients elsewhere.'],
    ],
  },
  labs: {
    badge: 'For laboratories & collection centres',
    title: 'Your lab.',
    titleAccent: '1,765 tests behind it.',
    lead: 'Send samples to our NABL-accredited central lab in Hyderabad, from routine chemistry to genomics, with scheduled pickups and partner pricing.',
    whyHeading: 'Why labs and collection centres work with us',
    whyLead: 'Send the tests you don’t run in-house to our NABL-accredited central lab, and offer your patients 1,765 tests.',
    cta: 'Sign up as a lab or collection centre',
    allow: ['lab', 'centre'],
    perks: [
      ['Daily sample pickup', 'Scheduled pickups with cold-chain transport to our central lab.'],
      ['Partner rate card', 'B2B pricing on routine, specialty and genomics tests.'],
      ['Reports into your workflow', 'Download from the partner portal, or connect to your LIS.'],
      ['Training & consumables', 'Collection SOPs, barcodes and kits from TrustLab, and status of every sample in the Partner app.'],
    ],
  },
  phlebotomists: {
    badge: 'For phlebotomists',
    title: 'Join as a Trustian.',
    titleAccent: 'Collect with care.',
    lead: 'Work flexible hours collecting samples at patients’ homes, with TrustLab training, kit and the Trustian app.',
    whyHeading: 'Why phlebotomists join TrustLab',
    whyLead: 'Trained phlebotomist? Join TrustLab as a Trustian and collect samples at patients’ homes in your area.',
    cta: 'Apply as a phlebotomist',
    allow: ['phlebo'],
    perks: [
      ['Flexible slots', 'Choose the hours and pincodes you cover.'],
      ['Paid per visit', 'Settled on a regular cycle, with the rate agreed up front.'],
      ['Training & kit', 'TrustLab collection SOPs, elderly-care training, collection kits and cold boxes.'],
      ['One app for the day', 'Bookings, directions and sample handover in the TrustLab Trustian app.'],
    ],
  },
  'managed-labs': {
    badge: 'Managed labs · HLM & independent labs',
    title: 'A TrustLab lab,',
    titleAccent: 'inside your hospital.',
    lead: 'For hospitals and independent labs. We set up, upgrade and run your laboratory to NABL standards, with specialty tests backed by our central lab.',
    whyHeading: 'What a managed lab includes',
    whyLead: 'For hospitals and independent labs. We set up, upgrade and run your laboratory to NABL standards, with specialty tests backed by our central lab.',
    cta: 'Talk to us about a managed lab',
    allow: ['hlm', 'independent'],
    perks: [
      ['Hospital Lab Management (HLM)', 'We set up, staff and run the laboratory inside your hospital.'],
      ['Independent labs', 'We bring equipment, validated methods and quality systems to your existing lab.'],
      ['Specialty via our central lab', 'Anything not run on site goes to our Hyderabad lab, with one report format.'],
      ['Model that suits you', 'Revenue share or fixed fee, agreed with you.'],
    ],
  },
};

const CREDENTIALS = [
  ['NABL', 'ISO 15189:2022 accredited'],
  ['1,765', 'Tests in our Directory of Services'],
  ['In-house', 'Genomics, cytogenetics, flow cytometry, histopathology'],
  ['14', 'Branches across India'],
];

const STEPS = [
  ['01', 'Sign up', 'Tell us who you are. It takes two minutes.'],
  ['02', 'We verify', 'Our partner team checks your registration and calls you.'],
  ['03', 'Account and rate card', 'Partner app access, pricing and pickup schedule.'],
  ['04', 'Start sending', 'Refer patients or send samples from day one.'],
];

const FALLBACK = { phone: '1800 599 5656', whatsapp: '+919014638633', email: 'admin@mytrustlab.com' };

const EMPTY = { name: '', org: '', reg: '', city: '', phone: '', email: '' };

// The labels that change with who is applying — the reference switches
// three of them off the selected type rather than showing a generic form.
const labelsFor = (type) => {
  if (type === 'doctor' || type === 'clinic') {
    return {
      org: 'Clinic or hospital (optional)',
      orgPh: 'Where you practise',
      reg: 'Medical registration no. (NMC/State)',
    };
  }
  if (type === 'phlebo') {
    return {
      org: 'Areas you can cover',
      orgPh: 'Pincodes or localities',
      reg: 'DMLT / phlebotomy certificate no.',
    };
  }
  return { org: 'Organisation name', orgPh: 'Registered name', reg: 'Establishment registration no.' };
};

// POST /partnerships has no column for the organisation or the
// registration number, so both are folded into `message` rather than
// dropped — the partner team needs them to verify. API-NOTES 2.12 asks
// for real fields.
const buildMessage = (form, type, labels) => [
  `Applying as: ${TYPES[type]}`,
  form.org.trim() ? `${labels.org}: ${form.org.trim()}` : null,
  form.reg.trim() ? `${labels.reg}: ${form.reg.trim()}` : null,
].filter(Boolean).join('\n');

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  minWidth: 0,
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const PartnerSegment = ({ segment }) => {
  const cfg = SEGMENTS[segment];
  const { data: contact } = useGetContactInfoQuery();
  const [submitPartnership, { isLoading: sending }] = useSubmitPartnershipInquiryMutation();

  const [type, setType] = useState(cfg.allow[0]);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const labels = useMemo(() => labelsFor(type), [type]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const phone = contact?.phone || FALLBACK.phone;
  const whatsapp = String(contact?.whatsapp || FALLBACK.whatsapp).replace(/\D/g, '');
  const email = contact?.email || FALLBACK.email;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Please enter your name');
    if (form.phone.replace(/\D/g, '').length < 10) return setError('Please enter a 10-digit mobile number');
    if (!form.city.trim()) return setError('Please enter your city');
    setError('');
    try {
      await submitPartnership({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        partnership_type: TYPES[type],
        message: buildMessage(form, type, labels),
      }).unwrap();
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err?.data?.message || 'Could not send — please try again.');
    }
    return undefined;
  };

  return (
    <Box>
      {/* ── hero ─────────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 360, md: 460 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt=""
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, rgba(7,33,29,0.94) 0%, rgba(7,33,29,0.76) 52%, rgba(7,33,29,0.25) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 860,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              p: 'clamp(28px, 5vw, 56px)',
              color: '#FFFFFF',
            }}
          >
            <Box
              component="span"
              sx={{
                alignSelf: 'start',
                px: '12px',
                py: '7px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {cfg.badge}
            </Box>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(34px, 5.8vw, 68px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              {cfg.title}{' '}
              <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>{cfg.titleAccent}</Box>
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 620 }}>
              {cfg.lead}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              <Box
                component="a"
                href="#signup"
                sx={{
                  p: '15px 22px',
                  borderRadius: '999px',
                  bgcolor: 'var(--tl-yellow)',
                  color: 'var(--tl-g9)',
                  fontFamily: UI,
                  fontSize: 16,
                  fontWeight: 800,
                  textDecoration: 'none',
                  '&:hover': { bgcolor: 'var(--tl-yellow-d)', color: 'var(--tl-g9)' },
                }}
              >
                Become a partner
              </Box>
              <Box
                component={Link}
                to={ROUTES.PARTNERS}
                sx={{
                  p: '14px 22px',
                  borderRadius: '999px',
                  border: '1.5px solid #5A7D75',
                  color: '#FFFFFF',
                  fontFamily: UI,
                  fontSize: 16,
                  fontWeight: 800,
                  textDecoration: 'none',
                  '&:hover': { borderColor: 'var(--tl-teal-l)', color: '#FFFFFF' },
                }}
              >
                All partner types
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box component="main" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}>
        {/* ── why ────────────────────────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            p: 'clamp(22px, 4vw, 40px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Typography
            component="h2"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(26px, 3.4vw, 40px)',
              lineHeight: 1,
              letterSpacing: '-.03em',
              color: '#FFFFFF',
            }}
          >
            {cfg.whyHeading}
          </Typography>
          <Typography sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.6, color: 'var(--tl-line-2)', maxWidth: 760 }}>
            {cfg.whyLead}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 1.25 }}>
            {cfg.perks.map(([t, d]) => (
              <Box
                key={t}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '22px', borderRadius: '24px', bgcolor: 'var(--tl-g7)' }}
              >
                <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800, color: 'var(--tl-teal-l)' }}>
                  {t}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: 'var(--tl-mint-2)' }}>
                  {d}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── credentials ────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
            gap: 1.5,
            mt: 4.5,
          }}
        >
          {CREDENTIALS.map(([v, l]) => (
            <Box key={l} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 3, borderRadius: '28px', bgcolor: '#FFFFFF' }}>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 40, lineHeight: 1, color: 'var(--tl-teal-d)' }}>
                {v}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                {l}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── how partnering works ───────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mt: 4.5,
            p: 'clamp(24px, 4vw, 48px)',
            borderRadius: '36px',
            bgcolor: '#FFFFFF',
          }}
        >
          <Typography
            component="h2"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.8vw, 48px)',
              lineHeight: 1,
              letterSpacing: '-.03em',
              color: 'var(--tl-g9)',
            }}
          >
            How partnering works
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 1.5 }}>
            {STEPS.map(([n, t, d]) => (
              <Box key={n} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: '22px', borderRadius: '24px', bgcolor: 'var(--tl-mint)' }}>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: 'var(--tl-teal-d)' }}>{n}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 18, fontWeight: 800, lineHeight: 1.25 }}>{t}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted)' }}>{d}</Box>
              </Box>
            ))}
          </Box>
          <Box
            component="a"
            href="#signup"
            sx={{
              alignSelf: 'start',
              p: '15px 22px',
              borderRadius: '999px',
              bgcolor: 'var(--tl-g9)',
              color: '#FFFFFF',
              fontFamily: UI,
              fontSize: 16,
              fontWeight: 800,
              textDecoration: 'none',
              '&:hover': { bgcolor: 'var(--tl-g8)', color: '#FFFFFF' },
            }}
          >
            {cfg.cta} →
          </Box>
        </Box>

        {/* ── cross-links ────────────────────────────────────────────── */}
        <Box component="section" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
          {[
            {
              to: ROUTES.FRANCHISE,
              t: 'Want to run a TrustLab-branded centre?',
              d: 'Open a TrustLab Care Centre as a franchise partner. →',
              bg: 'var(--tl-yellow)',
              fg: 'var(--tl-g9)',
              sub: '#3A4020',
            },
            {
              to: ROUTES.CORPORATE,
              t: 'Looking after employees?',
              d: 'Health checks and on-site camps for companies. →',
              bg: 'var(--tl-teal)',
              fg: '#FFFFFF',
              sub: 'var(--tl-teal-50)',
            },
          ].map((c) => (
            <Box
              key={c.to}
              component={Link}
              to={c.to}
              sx={{
                flex: '1 1 380px',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                p: 'clamp(24px, 4vw, 40px)',
                borderRadius: '32px',
                bgcolor: c.bg,
                color: c.fg,
                textDecoration: 'none',
                transition: `transform .35s ${EASE}`,
                '&:hover': { color: c.fg, transform: 'translateY(-3px)' },
              }}
            >
              <Box
                component="span"
                sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(22px, 2.6vw, 32px)', lineHeight: 1.05, letterSpacing: '-.02em' }}
              >
                {c.t}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: c.sub }}>
                {c.d}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── sign up ────────────────────────────────────────────────── */}
        <Box
          component="section"
          id="signup"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 1.5,
            mt: 6,
            scrollMarginTop: '100px',
          }}
        >
          <Box sx={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.75, p: 1 }}>
            <Typography
              component="h2"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(28px, 4vw, 48px)',
                lineHeight: 1.02,
                letterSpacing: '-.03em',
                color: 'var(--tl-g9)',
              }}
            >
              Become a partner.
            </Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
              Our partner team will verify your details, share the rate card and set up your
              account.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontFamily: UI, fontSize: 15, fontWeight: 700 }}>
              {[
                { t: phone, href: `tel:${String(phone).replace(/\D/g, '')}` },
                { t: `WhatsApp ${contact?.whatsapp || FALLBACK.whatsapp}`, href: `https://wa.me/${whatsapp}`, ext: true },
                { t: email, href: `mailto:${email}` },
              ].map((a) => (
                <Box
                  key={a.t}
                  component="a"
                  href={a.href}
                  {...(a.ext ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
                  sx={{ color: 'var(--tl-teal-d)', textDecoration: 'none', width: 'fit-content', '&:hover': { color: 'var(--tl-g9)' } }}
                >
                  {a.t}
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              flex: '1.4 1 440px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
              p: 'clamp(20px, 3.4vw, 32px)',
              borderRadius: '28px',
              bgcolor: '#FFFFFF',
            }}
          >
            {sent ? (
              <>
                <Box
                  component="span"
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: 'var(--tl-teal)',
                    color: '#FFFFFF',
                    fontSize: 24,
                    fontWeight: 800,
                  }}
                >
                  ✓
                </Box>
                <Box
                  component="span"
                  sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(24px, 3vw, 36px)', lineHeight: 1.05, letterSpacing: '-.02em' }}
                >
                  Thank you. We&rsquo;ve received your details.
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.65, color: 'var(--tl-muted)' }}>
                  Our partner team will call you to verify and set up your account. Meanwhile,
                  browse our{' '}
                  <Box component={Link} to={ROUTES.TESTS} sx={{ color: 'var(--tl-teal-d)', fontWeight: 800 }}>
                    Directory of Services
                  </Box>.
                </Box>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                    {cfg.allow.length > 1 ? 'I am a' : `Applying as a ${TYPES[cfg.allow[0]].toLowerCase()}`}
                  </Box>
                  {cfg.allow.length > 1 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {cfg.allow.map((k) => {
                        const on = k === type;
                        return (
                          <Box
                            key={k}
                            component="button"
                            type="button"
                            onClick={() => setType(k)}
                            aria-pressed={on}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: '999px',
                              p: '10px 16px',
                              whiteSpace: 'nowrap',
                              fontFamily: UI,
                              fontSize: 14,
                              fontWeight: 700,
                              border: `1.5px solid ${on ? 'var(--tl-g9)' : 'var(--tl-line)'}`,
                              bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
                              color: on ? '#FFFFFF' : 'var(--tl-g9)',
                            }}
                          >
                            {TYPES[k]}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                  <Box component="label" sx={fieldLabel}>
                    Your name
                    <Box component="input" value={form.name} onChange={set('name')} placeholder="Full name" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    {labels.org}
                    <Box component="input" value={form.org} onChange={set('org')} placeholder={labels.orgPh} sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    {labels.reg}
                    <Box component="input" value={form.reg} onChange={set('reg')} placeholder="Registration number" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    City
                    <Box component="input" value={form.city} onChange={set('city')} placeholder="e.g. Vijayawada" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Mobile
                    <Box component="input" value={form.phone} onChange={set('phone')} placeholder="+91" inputMode="tel" sx={field} />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Email
                    <Box component="input" value={form.email} onChange={set('email')} placeholder="name@clinic.com" type="email" sx={field} />
                  </Box>
                </Box>

                {error && (
                  <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#B23B37' }}>
                    {error}
                  </Box>
                )}

                <Box
                  component="button"
                  type="submit"
                  disabled={sending}
                  sx={{
                    alignSelf: 'start',
                    border: 0,
                    cursor: sending ? 'default' : 'pointer',
                    borderRadius: '16px',
                    p: '15px 22px',
                    bgcolor: sending ? '#B9C9C4' : 'var(--tl-g9)',
                    color: '#FFFFFF',
                    fontFamily: UI,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  {sending ? 'Sending…' : 'Submit for verification'}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerSegment;
